import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { Layout } from '../components/Layout';
import { MarkdownContent } from '../components/MarkdownContent';
import { StatusBadge } from '../components/StatusBadge';
import { cn } from '../lib/utils';
import { apiBase } from '../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Sparkles,
  FileText,
  Target,
  Users,
} from 'lucide-react';

export default function ConfirmProductDetails() {
  const { ecomId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [generatingNames, setGeneratingNames] = useState(false);

  const [winningDetail, setWinningDetail] = useState(null);
  const [marketing, setMarketing] = useState(null);

  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [nameSuggestions, setNameSuggestions] = useState([]);

  const [packageImageFile, setPackageImageFile] = useState(null);
  const [logoImageFile, setLogoImageFile] = useState(null);
  const [productImageFile, setProductImageFile] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [detailRes, marketingRes] = await Promise.all([
        supabase
          .from('winning_product_details')
          .select('*')
          .eq('ecom_id', ecomId)
          .single(),
        supabase
          .from('product_marketing_angles')
          .select('*')
          .eq('ecom_id', ecomId)
          .single(),
      ]);

      if (detailRes.error) throw detailRes.error;

      setWinningDetail(detailRes.data);
      setMarketing(marketingRes.data || null);
      setName(detailRes.data?.product_name || '');
    } catch (error) {
      console.error('Error loading confirm details page:', error);
      toast.error(t('confirm.errorLoad'));
    } finally {
      setLoading(false);
    }
  }, [ecomId, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleGenerateNames = async () => {
    console.log('[DEBUG] handleGenerateNames CALLED - button clicked');
    try {
      setGeneratingNames(true);
      console.log('[DEBUG] setGeneratingNames(true) called, spinner should show');
      
      const currentName = winningDetail?.product_name || name || '';
      const description = winningDetail?.description || '';
      const features = winningDetail?.features || '';
      const howToUse = winningDetail?.how_to_use || '';

      // Keep payload reasonable while still providing rich context
      const MAX_LEN = 2000;
      const trim = (value) =>
        value && value.length > MAX_LEN
          ? `${value.slice(0, MAX_LEN)}\n\n...[truncated]`
          : value || '';

      const payload = {
        name: currentName,
        description: trim(description),
        features: trim(features),
        how_to_use: trim(howToUse),
      };

      console.log('[DEBUG] About to call fetch /api/name-ideas with payload:', {
        nameLength: payload.name.length,
        descriptionLength: payload.description.length,
        featuresLength: payload.features.length,
        howToUseLength: payload.how_to_use.length,
      });

      const response = await fetch(`${apiBase}/api/name-ideas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      console.log('[DEBUG] fetch completed, response status:', response.status);

      if (!response.ok) {
        const text = await response.text();
        console.error('[DEBUG] Response not OK, text:', text);
        throw new Error(text || 'Failed to generate name ideas');
      }

      const data = await response.json();
      console.log('[DEBUG] Response JSON parsed:', data);
      
      const suggestions = data?.suggestions || [];

      if (!Array.isArray(suggestions) || !suggestions.length) {
        console.error('[DEBUG] No valid suggestions in response');
        toast.error(t('confirm.noNamesReturned'));
        return;
      }

      console.log('[DEBUG] Setting name suggestions:', suggestions);
      setNameSuggestions(suggestions.slice(0, 5));
      toast.success(t('confirm.namesGenerated'));
    } catch (error) {
      console.error('[DEBUG] ERROR in handleGenerateNames:', error);
      toast.error(t('confirm.errorGenerateNames'));
    } finally {
      console.log('[DEBUG] setGeneratingNames(false) called, spinner should hide');
      setGeneratingNames(false);
    }
  };

  const handleConfirm = async () => {
    if (!winningDetail) return;

    try {
      setSubmitting(true);
      const originalName = winningDetail.product_name || '';
      const detailsModified =
        (name && name !== originalName) || (notes && notes.trim().length > 0);

      // Upload images via backend (service key) if any are selected
      const hasAnyImage = packageImageFile || logoImageFile || productImageFile;
      let imageUrls = {
        package_image_url: null,
        logo_image_url: null,
        product_image_url: null,
        has_package_image: false,
        has_logo_image: false,
        has_product_image: false,
      };

      if (hasAnyImage) {
        const formData = new FormData();
        formData.append('ecom_id', ecomId);
        if (packageImageFile) {
          formData.append('package_image', packageImageFile);
        }
        if (logoImageFile) {
          formData.append('logo_image', logoImageFile);
        }
        if (productImageFile) {
          formData.append('product_image', productImageFile);
        }

        const uploadResponse = await fetch(`${apiBase}/api/upload-confirm-images`, {
          method: 'POST',
          body: formData,
        });

        if (!uploadResponse.ok) {
          const text = await uploadResponse.text();
          console.error('Error uploading images via backend:', text);
          toast.error(t('confirm.errorUploadImages') || 'Failed to upload images');
          throw new Error(text || 'Failed to upload images');
        }

        imageUrls = await uploadResponse.json();
      }

      // Send final confirmation via backend proxy, which forwards to n8n
      const response = await fetch(`${apiBase}/api/confirm-product`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ecom_id: ecomId,
          name: name || originalName,
          original_name: originalName,
          additional_notes: notes || null,
          details_modified: detailsModified,
          has_package_image: !!imageUrls.has_package_image,
          has_logo_image: !!imageUrls.has_logo_image,
          has_product_image: !!imageUrls.has_product_image,
          package_image_url: imageUrls.package_image_url,
          logo_image_url: imageUrls.logo_image_url,
          product_image_url: imageUrls.product_image_url,
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to submit confirmation');
      }

      toast.success(t('confirm.success'));
      navigate('/winning-products');
    } catch (error) {
      console.error('Error confirming product details:', error);
      toast.error(t('confirm.errorSubmit'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!winningDetail) {
    return (
      <Layout>
        <div className="text-center py-16">
          <p className="text-slate-600 mb-4">{t('confirm.notFound')}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-cyan-500 text-white rounded-lg"
          >
            {t('confirm.backHome')}
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout productName={t('confirm.title')}>
      <div className="max-w-5xl mx-auto" data-testid="confirm-product-details-page">
        {/* Header */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/winning-products')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-[#1E1B4B] font-['Manrope']">
                  {t('confirm.title')}
                </h1>
                <p className="text-slate-500 text-xs md:text-sm mt-1">
                  {t('confirm.subtitle', { ecomId })}
                </p>
              </div>
            </div>
            <StatusBadge status={winningDetail.status} lane="winning" />
          </div>
        </div>

        {/* Main content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Left: editable name + suggestions */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('confirm.nameLabel')}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-cyan-500 text-sm"
              />
              <p className="text-xs text-slate-500 mt-1">
                {t('confirm.nameHelp')}
              </p>
            </div>

            <div className="flex items-center justify-between gap-3">
              <div className="text-xs text-slate-500">
                {t('confirm.nameIdeasHint')}
              </div>
              <button
                type="button"
                onClick={handleGenerateNames}
                disabled={generatingNames}
                className={cn(
                  'inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold',
                  'bg-violet-500 text-white hover:bg-violet-600 disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {generatingNames ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    {t('confirm.generatingNames')}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-3 h-3" />
                    {t('confirm.generateNames')}
                  </>
                )}
              </button>
            </div>

            {nameSuggestions.length > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-700 mb-2">
                  {t('confirm.suggestionsTitle')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {nameSuggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setName(suggestion)}
                      className="px-3 py-1 rounded-full border border-violet-200 text-xs text-violet-700 bg-violet-50 hover:bg-violet-100"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('confirm.notesLabel')}
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-cyan-500 text-sm"
                placeholder={t('confirm.notesPlaceholder')}
              />
            </div>

            {/* Optional images */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 border-t border-slate-100">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Package image (optional)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPackageImageFile(e.target.files?.[0] || null)}
                  className="block w-full text-xs text-slate-600 file:text-xs file:px-2 file:py-1 file:mr-2 file:rounded-md file:border-0 file:bg-slate-100 file:text-slate-700"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Logo (optional)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setLogoImageFile(e.target.files?.[0] || null)}
                  className="block w-full text-xs text-slate-600 file:text-xs file:px-2 file:py-1 file:mr-2 file:rounded-md file:border-0 file:bg-slate-100 file:text-slate-700"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Product image (optional)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setProductImageFile(e.target.files?.[0] || null)}
                  className="block w-full text-xs text-slate-600 file:text-xs file:px-2 file:py-1 file:mr-2 file:rounded-md file:border-0 file:bg-slate-100 file:text-slate-700"
                />
              </div>
            </div>
          </div>

          {/* Right: read-only details */}
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                {t('confirm.descriptionLabel')}
              </h3>
              <MarkdownContent
                content={winningDetail.description}
                title={t('confirm.descriptionTitle')}
                maxHeight={180}
              />
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                {t('confirm.featuresLabel')}
              </h3>
              <MarkdownContent
                content={winningDetail.features}
                title={t('confirm.featuresTitle')}
                maxHeight={180}
              />
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-700 mb-2">
                {t('confirm.howToUseLabel')}
              </h3>
              <MarkdownContent
                content={winningDetail.how_to_use}
                title={t('confirm.howToUseTitle')}
                maxHeight={180}
              />
            </div>
          </div>
        </div>

        {/* Marketing context (read-only) */}
        {marketing && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mb-8 space-y-4">
            <h2 className="text-sm font-semibold text-slate-700">
              {t('confirm.marketingContext')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <h3 className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5">
                  <Target className="w-3 h-3" />
                  {t('confirm.marketingAngle')}
                </h3>
                <MarkdownContent
                  content={marketing.marketing_angle}
                  title={t('confirm.marketingAngle')}
                  maxHeight={120}
                />
              </div>
              <div>
                <h3 className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" />
                  {t('confirm.competitiveEdge')}
                </h3>
                <MarkdownContent
                  content={marketing.competitive_edge}
                  title={t('confirm.competitiveEdge')}
                  maxHeight={120}
                />
              </div>
              <div>
                <h3 className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5">
                  <Users className="w-3 h-3" />
                  {t('confirm.personas')}
                </h3>
                <MarkdownContent
                  content={marketing.customer_personas}
                  title={t('confirm.personas')}
                  maxHeight={120}
                />
              </div>
            </div>
          </div>
        )}

        {/* Confirm button */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className={cn(
              'inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold',
              'bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed',
              'shadow-lg shadow-emerald-500/30'
            )}
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('confirm.submitting')}
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                {t('confirm.confirmButton')}
              </>
            )}
          </button>
        </div>
      </div>
    </Layout>
  );
}

